import React from 'react';
import { Loader2, Lock } from 'lucide-react';
import useFeatureAccess, { FeatureAccessKey } from '../hooks/useFeatureAccess';

interface FeatureAccessGuardProps {
  featureKey: FeatureAccessKey;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export default function FeatureAccessGuard({ featureKey, children, fallback }: FeatureAccessGuardProps) {
  const { loading, hasAccess } = useFeatureAccess(featureKey);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }

    const featureNames: Record<FeatureAccessKey, string> = {
      feature_abstinence: 'Abstinenz-Management',
      feature_analytics: 'Statistiken',
      feature_accounting: 'Buchhaltung',
      feature_news: 'Nachrichten',
      feature_calendar: 'Kalender',
      feature_advanced_functions: 'Erweiterte Funktionen',
      feature_qm_manuals: 'QM-Handbücher',
      feature_file_upload: 'Datei-Upload',
      feature_email: 'E-Mail',
      feature_workflow: 'Workflow',
      feature_customizations: 'Anpassungen',
      feature_management: 'Verwaltung',
      feature_mpu: 'MPU',
      feature_personal: 'Personal',
    };

    const featureName = featureNames[featureKey] || 'dieses Feature';

    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
        <Lock className="h-10 w-10 text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Kein Zugriff</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md">
          Ihre Organisation verfügt nicht über den Zugriff auf das Feature &quot;{featureName}&quot;. Bitte wenden Sie sich an die
          Administration, um den Funktionsumfang zu erweitern.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
